import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

interface KPICardProps {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: LucideIcon;
    trend?: {
        value: number;
        isPositive: boolean;
    };
    color?: 'blue' | 'green' | 'purple' | 'orange' | 'red';
    delay?: number;
}

const colorVariants = {
    blue: {
        bg: 'bg-blue-500',
        light: 'bg-blue-50',
        text: 'text-blue-600',
        border: 'border-blue-200',
    },
    green: {
        bg: 'bg-green-500',
        light: 'bg-green-50',
        text: 'text-green-600',
        border: 'border-green-200',
    },
    purple: {
        bg: 'bg-purple-500',
        light: 'bg-purple-50',
        text: 'text-purple-600',
        border: 'border-purple-200',
    },
    orange: {
        bg: 'bg-orange-500',
        light: 'bg-orange-50',
        text: 'text-orange-600',
        border: 'border-orange-200',
    },
    red: {
        bg: 'bg-red-500',
        light: 'bg-red-50',
        text: 'text-red-600',
        border: 'border-red-200',
    },
};

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, icon: Icon, trend, color = 'blue', delay = 0 }) => {
    const colors = colorVariants[color];

    const formatValue = (val: string | number): string => {
        if (typeof val === 'number') {
            if (val >= 10000000) {
                return `${(val / 10000000).toFixed(1)}Cr`;
            } else if (val >= 100000) {
                return `${(val / 100000).toFixed(1)}L`;
            } else if (val >= 1000) {
                return `${(val / 1000).toFixed(1)}K`;
            }
            return val.toLocaleString();
        }
        return val;
    };

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay }} whileHover={{ y: -2 }} className="h-full">
            <Card className={`h-full border ${colors.border} hover:shadow-lg transition-shadow duration-300`}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-3">
                    <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
                    <div className={`p-2 rounded-lg ${colors.light}`}>
                        <Icon className={`h-5 w-5 ${colors.text}`} />
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col space-y-2">
                        <div className="text-2xl font-bold text-gray-900">{formatValue(value)}</div>

                        <div className="flex items-center justify-between">
                            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}

                            {trend && (
                                <div className={`flex items-center text-xs ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                    <span className="font-medium">
                                        {trend.isPositive ? '+' : ''}
                                        {trend.value}%
                                    </span>
                                    <span className="ml-1 text-gray-500">vs last month</span>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};

export default KPICard;
